/**
 * Eigene First-Party-Event-Pipeline neben GA4.
 *
 * - Queue + 5s-Flush + sofortiger Flush bei 20 Events
 * - Funktionale Events (signup/purchase/...) gehen IMMER raus
 * - Behavior-Events (pageview/click/...) NUR mit Cookie-Consent
 * - Beim Page-Hide: navigator.sendBeacon für nicht verlorenen Last-Batch
 *
 * Endpoint: POST /api/v1/events  (anonymous_id kommt aus httpOnly-Cookie)
 */

import { getConsent } from './analytics.service';

const ENDPOINT = '/api/v1/events';
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 20;
const MAX_QUEUE = 200;

const FUNCTIONAL_EVENTS = new Set([
  'signup_started', 'signup_completed',
  'login', 'logout',
  'purchase', 'payout_request_submitted'
]);

const SESSION_KEY = 'mn_session';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function safeUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (sollte praktisch nie greifen — alle Browser ab 2022 haben randomUUID)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getSessionId() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const { id, ts } = JSON.parse(raw);
      if (id && Date.now() - ts < SESSION_TIMEOUT_MS) {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
        return id;
      }
    }
  } catch (_) {}
  const id = safeUuid();
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
  } catch (_) {}
  return id;
}

const queue = [];
const pendingBehavior = [];
let timer = null;

function utmFromUrl() {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const out = {};
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const v = p.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function buildEvent(name, props) {
  return {
    name,
    session_id: getSessionId(),
    url: typeof window !== 'undefined' ? window.location.href : null,
    path: typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    props: props || {},
    ...utmFromUrl()
  };
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

function enqueue(ev) {
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push(ev);
  if (queue.length >= FLUSH_BATCH_SIZE) flush();
  else scheduleFlush();
}

export function track(name, props = {}) {
  if (FUNCTIONAL_EVENTS.has(name)) {
    enqueue(buildEvent(name, props));
    return;
  }
  const c = getConsent();
  if (c && c.analytics) {
    enqueue(buildEvent(name, props));
  } else if (pendingBehavior.length < MAX_QUEUE) {
    pendingBehavior.push(buildEvent(name, props));
  }
}

export function onConsentGranted() {
  while (pendingBehavior.length) enqueue(pendingBehavior.shift());
}

export function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length) return;
  const batch = queue.splice(0, FLUSH_BATCH_SIZE);
  fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events: batch }),
    keepalive: true
  }).catch(() => {});
}

export function flushBeacon() {
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  const payload = JSON.stringify({ events: batch });
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(ENDPOINT, blob);
  } else {
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true
    }).catch(() => {});
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushBeacon);
  window.addEventListener('beforeunload', flushBeacon);
}

// Test-Hook
export function __resetForTests() {
  queue.length = 0;
  pendingBehavior.length = 0;
  if (timer) { clearTimeout(timer); timer = null; }
}

const eventTracking = { track, flush, flushBeacon, onConsentGranted };
export default eventTracking;
